import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Eye, Paperclip } from "lucide-react";

import { DataTable } from "components/ui/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { EmailDetailModal } from "features/communications/components/EmailDetailModal/EmailDetailModal";
import { PatientNameLink } from "features/communications/components/PatientNameLink/PatientNameLink";
import { usePaginatedEmails } from "features/communications/hooks/usePaginatedEmails";

import styles from "./CommunicationsTable.module.css";

const ROWS_OPTIONS = [10, 20, 50];

const STATUS_PILL = {
  SENT: PILL_COLORS.statusSent,
  PENDING: PILL_COLORS.statusPending,
  FAILED: PILL_COLORS.statusError,
};

const formatStatus = (status) => {
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd MMM yyyy HH:mm");
  } catch {
    return value;
  }
};


const countAttachments = (documentIds) => {
  if (!documentIds) return 0;
  return documentIds
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean).length;
};

/*
 * Two date fields in the table's own filter menu. DataTableFilter renders `type: "date"`
 * with a DatePickerField and supplies its own "Clear filters", so nothing here needs a
 * Search or Clear button of its own.
 */
const FILTER_GROUPS = [
  { id: "startDate", label: "From", type: "date" },
  { id: "endDate", label: "To", type: "date" },
];

export const CommunicationsTable = ({
  patientId,
  hidePatientColumn = false,
  hideSentByColumn = false,
  // Named by the caller, because the same table is the patient's Email tab and the
  // practice-wide Communications page, which are not the same list.
  title = "Email",
}) => {
  const [applied, setApplied] = useState({ startDate: "", endDate: "" });
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(10);
  const [selectedEmailId, setSelectedEmailId] = useState(null);

  const { data, isLoading, isError } = usePaginatedEmails({
    patientId,
    startDate: applied.startDate,
    endDate: applied.endDate,
    page,
    rows,
  });

  const emails = data?.data ?? [];
  const totalCount = data?.totalRecords ?? data?.totalCount ?? data?.total ?? data?.totalElements;

  // Applied straight away: the filter menu is a dropdown the user closes when done, so a
  // separate Search press would be a second confirmation of a choice already made.
  const handleFilterChange = (groupId, value) => {
    setApplied((prev) => ({ ...prev, [groupId]: value }));
    setPage(1);
  };

  const rowActions = useMemo(
    () => [
      {
        label: "View",
        icon: Eye,
        primary: true,
        onClick: (email) => setSelectedEmailId(email.id),
      },
    ],
    []
  );

  const columns = useMemo(() => {
    const all = [
      {
        id: "dateSent",
        header: "Date Sent",
        // "09 Jun 2026 12:09" needs about 165px; at 130 it truncated to
        // "31 Mar 2026 22:…", losing the time on every row that carried one.
        width: "170px",
        render: (email) => formatDateTime(email.sentAt || email.created),
      },
      !hidePatientColumn && {
        id: "patient",
        header: "Patient",
        width: "100px",
        flexGrow: 1,
        render: (email) => <PatientNameLink patientId={email.patientId} />,
      },
      {
        id: "recipient",
        header: "Recipient Email",
        width: "140px",
        flexGrow: 1.5,
        getTooltipText: (email) => email.emailAddress,
        render: (email) => email.emailAddress || "—",
      },
      {
        id: "subject",
        header: "Subject",
        width: "120px",
        flexGrow: 2,
        render: (email) => email.subject || "—",
      },
      !hideSentByColumn && {
        id: "sentBy",
        header: "Sent By",
        width: "95px",
        flexGrow: 1,
        render: (email) => {
          const { sentBy, createdBy } = email;
          if (sentBy?.firstName || sentBy?.lastName) {
            return `${sentBy.firstName || ""} ${sentBy.lastName || ""}`.trim();
          }

          return createdBy || "—";
        },
      },
      {
        id: "status",
        header: "Status",
        width: "80px",
        render: (email) => {
          const key = (email.status || "").toUpperCase();

          return (
            <Pill {...(STATUS_PILL[key] ?? PILL_COLORS.statusNoStatus)}>
              {formatStatus(email.status)}
            </Pill>
          );
        },
      },
      {
        id: "attachments",
        header: "Attachments",
        width: "95px",
        render: (email) => {
          const count = countAttachments(email.documentIds);

          if (!count) return <span className={styles.emptyCell}>—</span>;

          return (
            <span className={styles.attachmentCount}>
              <Paperclip size={13} />
              {count}
            </span>
          );
        },
      },
    ].filter(Boolean);

    return all;
  }, [hidePatientColumn, hideSentByColumn]);

  return (
    <div className={styles.layout} data-testid="communications-table">
      <div className={styles.tableCard}>
      {/* Every tab in this area names itself; this one was the only list with no
          heading at all. */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{title}</h2>
      </div>

      {isError && (
        <div className={styles.errorBanner} data-testid="communications-error">
          Failed to load emails. Please try again.
        </div>
      )}

      <DataTable
        columns={columns}
        data={emails}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        /*
         * The date range moved into the table's own filter menu rather than sitting in a
         * card above it (KIN review). DataTableFilter already supports `type: "date"` and
         * draws its own "Clear filters", so the separate Search/Clear pair — and the extra
         * card holding them — were duplicating machinery the table brings with it.
         */
        filters={{ value: applied, onChange: handleFilterChange }}
        filtersConfig={FILTER_GROUPS}
        emptyTitle="No emails sent yet"
        emptyText="No emails match the current filters."
        totalCount={totalCount}
        currentPage={page}
        onPageChange={setPage}
        rowsValue={rows}
        onRowsChange={(value) => {
          setRows(value);
          setPage(1);
        }}
        rowsOptions={ROWS_OPTIONS}
        rowActions={rowActions}
      />
      </div>

      <EmailDetailModal
        emailId={selectedEmailId}
        onClose={() => setSelectedEmailId(null)}
      />
    </div>
  );
};
